// Paginated ledger with URL-backed day/month filters and date groups; selection display is independent of receipt results.
import { useCallback, useMemo, useState } from 'react';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useWorkspaceStore } from '@/stores/workspace';
import { useLocation, Link, useSearchParams } from 'react-router-dom';
import { api } from '@/lib/api';
import { countLedgerFilters, defaultLedgerFilters, groupLedgerReceipts, ledgerTodayRange, ledgerMonthValue, ledgerAssistantScope, readLedgerFilters, writeLedgerFilters, type LedgerFilters as Filters } from '@/lib/ledgerFilters';
import { cn, formatCurrency, formatDate } from '@/lib/utils';
import { ArrowUpRight, Filter, Plus, Receipt as ReceiptIcon, Landmark, Folders, Search, ShoppingBasket } from 'lucide-react';
import { AppDrawer } from '@/components/ui/AppDrawer';
import { LedgerDatePicker } from '@/components/LedgerDatePicker';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { EmptyState } from '@/components/ui/EmptyState';
import { AdminIcon } from '@/components/ui/AdminIcon';
import { PageLayout, PageLink } from '@/components/PageLayout';
import { useListPosition } from '@/hooks/useListPosition';
import { AskAiButton } from '@/components/chat/AskAiButton';

interface ReceiptListItem {
  id: string;
  merchant: string | null;
  merchant_zh: string | null;
  receipt_date: string | null;
  currency: string;
  total_amount: number;
  base_amount_minor?: number | null;
  valuation_status?: string;
  category_id: string | null;
  image_path: string | null;
  extraction_status: 'pending' | 'success' | 'failed' | 'manual';
  ai_confidence: number | null;
  is_verified: number;
  recorded_by: string;
  spender_user_id: string | null;
  created_at: number;
  is_income: number;
  income_source_id: string | null;
  fixed_expense_id: string | null;
  source: string | null;
  booking_evidence?: 'scheduled' | 'user';
}

interface CategoryRow {
  id: string;
  name: string;
  name_zh: string | null;
  icon: string | null;
  is_system: number;
  sort_order: number;
}

interface MemberRow {
  id: string;
  email: string;
  name: string | null;
  role: 'owner' | 'member';
}

/** URL state restores both calendar scope and other filters after a receipt detail visit. */
function useUrlFilters(): [Filters, (next: Filters | ((prev: Filters) => Filters)) => void] {
  const [params, setParams] = useSearchParams();
  const filters = useMemo(() => readLedgerFilters(params), [params]);
  const setFilters = useCallback((next: Filters | ((prev: Filters) => Filters)) => {
    setParams(prev => {
      const current = readLedgerFilters(prev);
      return writeLedgerFilters(prev, typeof next === 'function' ? next(current) : next);
    }, { replace: true });
  }, [setParams]);
  return [filters, setFilters];
}
const PAGE_SIZE = 30;

function buildReceiptQuery(filters: Filters, offset: number): string {
  const qs = new URLSearchParams();
  if (filters.from) qs.set('from', filters.from);
  if (filters.to) qs.set('to', filters.to);
  if (filters.category_id) qs.set('category_id', filters.category_id);
  if (filters.recorded_by) qs.set('recorded_by', filters.recorded_by);
  if (filters.spender_user_id) qs.set('spender_user_id', filters.spender_user_id);
  if (filters.q) qs.set('q', filters.q);
  if (filters.status) qs.set('status', filters.status);
  if (filters.kind) qs.set('kind', filters.kind);
  qs.set('limit', String(PAGE_SIZE));
  qs.set('offset', String(offset));
  return `?${qs.toString()}`;
}

export function LedgerPage() {
  const { t, i18n } = useTranslation();
  const ws = useWorkspaceStore((s) => s.current);
  const location = useLocation();
  const en = i18n.language.startsWith('en');

  const [filters, setFilters] = useUrlFilters();
  const [showFilters, setShowFilters] = useState(false);
  const activeFilters = countLedgerFilters(filters);
  const selectedMonth = ledgerMonthValue(filters);
  const selectedDay = filters.from && filters.from === filters.to ? filters.from : '';
  const todayRange = ledgerTodayRange();
  const isTodaySelected = filters.from === todayRange.from && filters.to === todayRange.to;

  const receiptsQuery = useInfiniteQuery({
    // v2 suffix: previous shape was useQuery (flat array); migrating to
    // useInfiniteQuery changes the cached shape to { pages, pageParams }.
    // Persisted cache from older deploys would crash on read — busting the
    // key forces a fresh load.
    queryKey: ['receipts-v2', ws?.id, filters],
    queryFn: ({ pageParam }) =>
      api.get<ReceiptListItem[]>(`/receipts${buildReceiptQuery(filters, pageParam ?? 0)}`),
    enabled: !!ws,
    initialPageParam: 0 as number,
    getNextPageParam: (lastPage, allPages) => {
      if (!Array.isArray(lastPage) || lastPage.length < PAGE_SIZE) return undefined;
      return allPages.reduce((sum, p) => sum + (Array.isArray(p) ? p.length : 0), 0);
    },
  });
  const receipts = receiptsQuery.data?.pages.flat() ?? [];
  const receiptsLoading = receiptsQuery.isLoading;
  const receiptGroups = groupLedgerReceipts(receipts);
  useListPosition(`ledger:${ws?.id}:${location.search}`, !!receiptsQuery.data);

  const { data: categories } = useQuery({
    queryKey: ['categories', ws?.id],
    queryFn: () => api.get<CategoryRow[]>('/categories'),
    enabled: !!ws,
    staleTime: 5 * 60 * 1000,
  });

  const { data: members } = useQuery({
    queryKey: ['members', ws?.id],
    queryFn: () => api.get<MemberRow[]>(`/workspaces/${ws!.id}/members`),
    enabled: !!ws,
    staleTime: 5 * 60 * 1000,
  });

  // Workspace-wide set of receipt IDs that have ≥1 potential duplicate.
  // Used to render a 🔁 chip on the row so the user spots collisions from
  // the ledger instead of having to drill into each receipt detail page.
  // Key starts with 'receipts' so all the broader receipt-derived
  // invalidations (alias backfill, receipt edit/delete) hit this too.
  const { data: duplicates } = useQuery({
    queryKey: ['receipts', 'duplicates', ws?.id],
    queryFn: () => api.get<{ ids: string[]; count: number }>('/receipts/duplicates'),
    enabled: !!ws,
    staleTime: 60 * 1000,
  });
  const duplicateSet = useMemo(
    () => new Set(duplicates?.ids ?? []),
    [duplicates],
  );

  return (
    <PageLayout root title={en ? 'Ledger' : '账本'}>
      <section className="space-y-2 rounded-2xl border bg-card p-2.5 shadow-card" aria-label={en ? 'Ledger dates' : '账本日期'}>
        <LedgerDatePicker selectedMonth={selectedMonth} selectedDay={selectedDay} en={en}
          fallbackLabel={filters.from || filters.to ? `${filters.from || (en ? 'Any start' : '不限起始')} — ${filters.to || (en ? 'Any end' : '不限结束')}` : (en ? 'All dates' : '全部日期')}
          onSelect={range => setFilters(current => ({ ...current, ...range }))} />
          <div className="grid grid-cols-3 gap-2" aria-label={en ? 'Quick date filters' : '时间快捷选项'}>
            <Button variant={isTodaySelected ? 'primary' : 'secondary'} size="sm" className="h-11 w-full rounded-lg px-2 text-xs" aria-pressed={isTodaySelected} onClick={() => { const range = ledgerTodayRange(); setFilters(current => ({ ...current, ...range })); }}>{en ? 'Today' : '今天'}</Button>
            <Button variant="secondary" size="sm" className="h-11 w-full rounded-lg px-2 text-xs" onClick={() => { const range = defaultLedgerFilters(); setFilters(current => ({ ...current, from: range.from, to: range.to })); }}>{en ? 'This month' : '本月'}</Button>
            <Button variant={!filters.from && !filters.to ? 'primary' : 'secondary'} size="sm" className="h-11 w-full rounded-lg px-2 text-xs" aria-pressed={!filters.from && !filters.to} onClick={() => setFilters(current => ({ ...current, from: '', to: '' }))}>{en ? 'All dates' : '全部日期'}</Button>
          </div>
        {!selectedMonth && <p className="text-xs text-muted-foreground">{selectedDay ? `${isTodaySelected ? (en ? 'Today · ' : '今天 · ') : ''}${selectedDay}` : filters.from || filters.to ? `${filters.from || (en ? 'Any start' : '不限起始')} — ${filters.to || (en ? 'Any end' : '不限结束')}` : (en ? 'All dates · includes older records' : '全部日期 · 包含历史记录')}</p>}
        {(filters.status || filters.kind) && <p className="text-xs leading-relaxed text-muted-foreground">{en ? 'AI analyzes booked spending in this range; review status and income/expense tabs are not inherited.' : 'AI 分析此范围的已入账支出，不继承核对状态与收支类型选项。'}</p>}
      </section>
      <div className="flex items-center gap-2">
        <label className="block min-w-0 flex-1 text-xs"><span className="sr-only">{en ? 'Search merchant or note' : '搜索商家或备注'}</span><span className="relative block"><Search className="pointer-events-none absolute left-3 top-3.5 h-4 w-4 text-muted-foreground" aria-hidden /><input type="search" aria-label={en ? 'Search ledger' : '搜索账本'} value={filters.q} onChange={event => setFilters(current => ({...current, q:event.target.value}))} placeholder={t('home.filterSearchPlaceholder')} className="min-h-[44px] w-full min-w-0 rounded-xl border bg-card py-2 pl-9 pr-3 text-base" /></span></label>
        <Button variant={activeFilters > 0 ? 'soft' : 'secondary'} className="shrink-0 rounded-xl px-3" onClick={() => setShowFilters(v => !v)}><Filter className="h-4 w-4" aria-hidden />{t('home.filters')}{activeFilters > 0 ? ` (${activeFilters})` : ''}</Button>
      </div>
      <div className="grid grid-cols-2 gap-2 min-[360px]:grid-cols-4" aria-label={en ? 'Record type' : '账单类型'}>
        {([{id:'',zh:'全部',en:'All'},{id:'expense',zh:'支出',en:'Expenses'},{id:'income',zh:'收入',en:'Income'},{id:'pending',zh:'待核对',en:'To review'}] as const).map(item => <button type="button" key={item.id} aria-pressed={item.id==='pending'?filters.status==='pending':filters.status===''&&filters.kind===item.id} onClick={() => setFilters(current => ({...current,status:item.id==='pending'?'pending':'',kind:item.id==='pending'?'':item.id}))} className={cn('min-h-[44px] min-w-0 rounded-xl border px-1 py-2 text-xs font-semibold transition-colors sm:text-sm', (item.id==='pending'?filters.status==='pending':filters.status===''&&filters.kind===item.id)?'border-transparent bg-primary text-primary-foreground shadow-sm':'border-border bg-card text-muted-foreground hover:bg-muted')}>{en?item.en:item.zh}</button>)}
      </div>

        <AppDrawer
          open={showFilters}
          onOpenChange={setShowFilters}
          title={t('home.filters')}
        >
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-2">
              <label className="block text-xs">
                <span className="mb-1 block text-muted-foreground">{t('home.filterFrom')}</span>
                <input
                  type="date"
                  value={filters.from}
                  onChange={(e) => setFilters((f) => ({ ...f, from: e.target.value }))}
                  className="w-full rounded-md border bg-background px-2 py-1.5 text-sm"
                />
              </label>
              <label className="block text-xs">
                <span className="mb-1 block text-muted-foreground">{t('home.filterTo')}</span>
                <input
                  type="date"
                  value={filters.to}
                  onChange={(e) => setFilters((f) => ({ ...f, to: e.target.value }))}
                  className="w-full rounded-md border bg-background px-2 py-1.5 text-sm"
                />
              </label>
            </div>
            <label className="block text-xs">
              <span className="mb-1 block text-muted-foreground">{t('home.filterCategory')}</span>
              <select
                value={filters.category_id}
                onChange={(e) => setFilters((f) => ({ ...f, category_id: e.target.value }))}
                className="w-full rounded-md border bg-background px-2 py-1.5 text-sm"
              >
                <option value="">{t('home.filterAllCategories')}</option>
                {categories?.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.icon ? `${c.icon} ` : ''}
                    {c.name}
                    {c.name_zh ? ` / ${c.name_zh}` : ''}
                  </option>
                ))}
              </select>
            </label>
            <div className="grid grid-cols-2 gap-2">
              <label className="block text-xs">
                <span className="mb-1 block text-muted-foreground">{t('home.filterRecordedBy')}</span>
                <select
                  value={filters.recorded_by}
                  onChange={(e) =>
                    setFilters((f) => ({ ...f, recorded_by: e.target.value }))
                  }
                  className="w-full rounded-md border bg-background px-2 py-1.5 text-sm"
                >
                  <option value="">{t('home.filterAnyone')}</option>
                  {members?.map((m) => (
                    <option key={m.id} value={m.id}>
                      {m.name?.trim() || m.email}
                    </option>
                  ))}
                </select>
              </label>
              <label className="block text-xs">
                <span className="mb-1 block text-muted-foreground">{t('home.filterSpentBy')}</span>
                <select
                  value={filters.spender_user_id}
                  onChange={(e) =>
                    setFilters((f) => ({ ...f, spender_user_id: e.target.value }))
                  }
                  className="w-full rounded-md border bg-background px-2 py-1.5 text-sm"
                >
                  <option value="">{t('home.filterAnyone')}</option>
                  {members?.map((m) => (
                    <option key={m.id} value={m.id}>
                      {m.name?.trim() || m.email}
                    </option>
                  ))}
                </select>
              </label>
            </div>
            <label className="block text-xs">
              <span className="mb-1 block text-muted-foreground">{t('home.filterSearch')}</span>
              <input
                type="search"
                inputMode="search"
                enterKeyHint="search"
                value={filters.q}
                onChange={(e) => setFilters((f) => ({ ...f, q: e.target.value }))}
                placeholder={t('home.filterSearchPlaceholder')}
                className="w-full rounded-md border bg-background px-2 py-1.5 text-sm"
              />
            </label>
            {activeFilters > 0 && (
              <button
                onClick={() => setFilters(defaultLedgerFilters())}
                className="flex min-h-[44px] w-full items-center justify-center rounded-md border text-xs text-muted-foreground hover:bg-muted"
              >
                {t('home.filterClearAll')}
              </button>
            )}
          </div>
        </AppDrawer>

        {receiptsQuery.isError ? <div role="alert" className="rounded-xl border bg-card p-4 text-sm"><p>{(receiptsQuery.error as Error).message}</p><button className="mt-2 min-h-[44px] underline" onClick={() => void receiptsQuery.refetch()}>{en?'Retry':'重试'}</button></div> : receiptsLoading ? (
          <div className="rounded-xl border bg-card p-8 text-center text-sm text-ink-500 shadow-card">
            {t('home.loading')}
          </div>
        ) : !receipts || receipts.length === 0 ? (
          activeFilters > 0 ? (
            <div className="rounded-xl border bg-card shadow-card">
              <EmptyState
                icon={<Filter className="h-5 w-5" />}
                title={selectedDay ? (en ? `No matching records on ${selectedDay}` : `${selectedDay} 暂无匹配账单`) : t('home.noMatchHeading')}
                description={t('home.noMatchBody')}
                action={
                  <Button
                    variant="secondary"
                    size="md"
                    onClick={() => setFilters(defaultLedgerFilters())}
                  >
                    {t('home.filterClear')}
                  </Button>
                }
              />
            </div>
          ) : (
            <div className="rounded-xl border bg-card shadow-card">
              <EmptyState
                icon={<ReceiptIcon className="h-5 w-5" />}
                title={en ? 'No records this month' : '本月暂无账单'}
                description={en ? 'Choose another month to view history, or add a new record.' : '可切换月份查看历史，或记一笔开始记录。'}
                action={
                  <Button asChild variant="primary" size="md">
                    <Link to="/capture">
                      <Plus className="h-4 w-4" aria-hidden />
                      {t('home.emptyScanCta')}
                    </Link>
                  </Button>
                }
              />
            </div>
          )
        ) : (
          <>
            {/* Date groups retain server ordering and each receipt's evidence;
                the amount wraps below its merchant only when space requires it. */}
            <div className="space-y-3">
              <div className="flex flex-wrap items-center justify-between gap-2 px-1">
                <span className="text-sm font-semibold text-foreground">
                  {t('home.receiptsTableHeader')}
                </span>
                <span className="text-xs text-muted-foreground">
                  {en ? `${receipts.length} loaded` : `已加载 ${receipts.length} 笔`}
                </span>
              </div>
              {receiptGroups.map(group => <section key={group.date} aria-label={group.date || (en ? 'Unknown date' : '日期未知')} className="overflow-hidden rounded-2xl border border-border bg-card shadow-card">
                <h2 className="border-b border-border bg-muted/70 px-4 py-3 text-xs font-semibold text-foreground">{group.date ? formatDate(group.date) : (en ? 'Unknown date' : '日期未知')}</h2>
                <ul className="divide-y divide-border">
                {group.items.map((r) => {
                  const dateStr = r.receipt_date
                    ? formatDate(r.receipt_date)
                    : formatDate(new Date(r.created_at * 1000));
                  const verified = !!r.is_verified;
                  const lowConfidence =
                    !verified &&
                    r.extraction_status === 'success' &&
                    r.ai_confidence != null &&
                    r.ai_confidence < 0.6;
                  const cat = categories?.find((c) => c.id === r.category_id);
                  // Keep real category/custom emoji rather than assigning a
                  // category from merchant text. Unknown records use a receipt.
                  const leadingIcon = cat?.icon ?? null;
                  return (
                    <li key={r.id}>
                      <Link
                        to={`/receipts/${r.id}`} state={{returnTo:location.pathname+location.search}}
                        className="flex min-h-[44px] items-start gap-3 px-3 py-4 transition-colors hover:bg-muted/50 sm:px-4"
                      >
                        {r.is_income ? <AdminIcon icon={ArrowUpRight} tone="success" className="h-10 w-10 shrink-0" /> : leadingIcon ? (
                          <span
                            className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border border-primary/10 bg-primary-soft-bg text-xl leading-none"
                            aria-label={cat?.name}
                            title={
                              cat?.name_zh
                                  ? `${cat?.name} ${cat?.name_zh}`
                                  : cat?.name
                            }
                          >
                            {leadingIcon}
                          </span>
                        ) : (
                          <AdminIcon icon={ReceiptIcon} tone="neutral" className="h-10 w-10 shrink-0" />
                        )}
                        <div className="min-w-0 flex-1">
                          <div className="flex flex-wrap items-start justify-between gap-x-3 gap-y-1">
                            {/* 0.3.4: line-clamp-1 → line-clamp-2 so long
                                merchant strings ("WOOLWORTHS WESTFIELD
                                PACIFIC FAIR") wrap instead of clipping. */}
                            <span className="line-clamp-2 min-w-[6rem] flex-1 break-words text-sm font-semibold text-foreground">
                              {r.merchant ?? t('home.unknownMerchant')}
                            </span>
                            <span
                              className={cn(
                                'ml-auto max-w-full break-all text-right text-sm font-bold tracking-tight tabular-nums',
                                r.is_income === 1 && 'text-income',
                              )}
                            >
                              {r.is_income === 1 ? '+' : ''}
                              {r.currency === 'AUD' ? formatCurrency((r.base_amount_minor ?? Math.round(r.total_amount * 100)) / 100, 'AUD') : <span className="block text-right"><span className="block">{r.base_amount_minor == null ? t('money.missing', { defaultValue: '待折算 AUD' }) : `${r.valuation_status === 'estimated' ? '≈ ' : ''}AUD ${(r.base_amount_minor / 100).toFixed(2)}`}</span><span className="mt-1 block text-xs font-normal text-muted-foreground">{r.currency} {r.total_amount.toFixed(2)}</span></span>}
                            </span>
                          </div>
                          <div className="mt-1 flex flex-wrap items-start gap-x-2 gap-y-1">
                            {r.is_income === 1 && (
                              <Badge variant="success" size="sm">
                                {t('home.incomeBadge')}
                              </Badge>
                            )}
                            {r.extraction_status === 'failed' && (
                              <Badge variant="critical" size="sm">
                                {t('home.extractionFailed')}
                              </Badge>
                            )}
                            {lowConfidence && (
                              <Badge variant="warn" size="sm">
                                {t('home.lowConfidence')}
                              </Badge>
                            )}
                            {duplicateSet.has(r.id) && (
                              <Badge
                                variant="warn"
                                size="sm"
                                title={t('home.duplicateBadgeHint')}
                              >
                                {t('home.duplicateBadge')}
                              </Badge>
                            )}
                            {r.booking_evidence === 'scheduled' && <Badge variant="neutral" size="sm" title={i18n.language.startsWith('en')?'Booked from schedule; bank payment not verified':'按固定安排入账，尚未核对银行付款'}>{i18n.language.startsWith('en')?'Auto booked':'自动入账'}</Badge>}
                            {verified && r.booking_evidence !== 'scheduled' && (
                              <Badge variant="success" size="sm">
                                ✓
                              </Badge>
                            )}
                          </div>
                          {/* 0.3.4: date and merchant_zh used to share
                              one inline row with a `·` separator — long CJK
                              merchant translations ("佐赞烤肉串 霍恩斯比")
                              truncated on 375px mobile. Now each occupies
                              its own line so both stay fully readable. */}
                          <div className="mt-1 space-y-0.5 text-xs text-muted-foreground">
                            <div className="tabular-nums">{dateStr}</div>
                            {r.merchant_zh && (
                              <div className="line-clamp-2">{r.merchant_zh}</div>
                            )}
                          </div>
                        </div>
                      </Link>
                    </li>
                  );
                })}
                </ul>
              </section>)}
            </div>
            {receiptsQuery.hasNextPage && (
              <div className="flex justify-center pt-1">
                <Button
                  variant="secondary"
                  size="md"
                  loading={receiptsQuery.isFetchingNextPage}
                  onClick={() => receiptsQuery.fetchNextPage()}
                >
                  {t('home.loadMore')}
                </Button>
              </div>
            )}
          </>
        )}

      <section className="grid grid-cols-2 gap-2" aria-label={en ? 'Ledger tools' : '账本工具'}>
        <Link to="/ledger/reconcile" className="flex min-h-[44px] min-w-0 items-center justify-center gap-2 rounded-xl border bg-card p-2 text-xs font-medium"><Landmark className="h-4 w-4 shrink-0 text-primary-soft-fg" aria-hidden /><span className="min-w-0 break-words">{en?'Bank reconciliation':'银行核对'}</span></Link>
        <Link to="/ledger/groups" className="flex min-h-[44px] min-w-0 items-center justify-center gap-2 rounded-xl border bg-card p-2 text-xs font-medium"><Folders className="h-4 w-4 shrink-0 text-primary-soft-fg" aria-hidden /><span className="min-w-0 break-words">{en?'Group ledgers':'群组账本'}</span></Link>
        <div className="col-span-2 flex min-w-0 flex-wrap justify-end gap-2"><AskAiButton scope={ledgerAssistantScope(filters)} className="inline-flex min-h-[44px] max-w-full items-center gap-1 rounded-xl border bg-card px-3 text-sm hover:bg-muted" /></div>
      </section>
      <PageLink to="/shopping" title={en?'Shopping lists':'购物清单'} description={en?'Plan your purchases':'规划采购'} icon={ShoppingBasket}/>
      <PageLink to="/price-check" title={en?'Price check':'商品比价'} description={en?'Compare product prices before buying':'购买前查询和比较价格'} icon={ShoppingBasket}/>
    </PageLayout>
  );
}
